import { useIsFocused } from "@react-navigation/native";
import * as DocumentPicker from "expo-document-picker";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import _ from "lodash";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/redux";
import api from "src/util/api";
import { getApiError, putBrand, putRep, putRetailer, putRetailerReleaseCredit } from "src/util/apiNew";
import { getSafeUrl } from "src/util/functions";
import { tBoutique, tBrand, tRep, tUser, validate } from "tmt_api/src/types/types.validator";
import { tAdminUserState } from "../AdminUser/types";
import { handleErrorEvents } from "../errorHandler/actions";
import { tReduxAction } from "../types";
import { tAccountType, tAdminCompanyState } from "./types";

export const setBrandInfo = (brandId: string): tReduxAction => {
  return (dispatch) => {
    api
      .get({ endpoint: `/admin/brand/${brandId}` })
      .then((myData) => {
        const myBrand = validate("tBrand")(myData.data);
        dispatch({ type: "ADMIN_SET_COMPANY", company: { ...myBrand, type: "brand" } });
      })
      .catch((e) => {
        console.error(e);
        dispatch({ type: "RIBBON", message: new Error("Failed to retrieve Brand information") });
      });
  };
};

export const fetchCompanyData = (
  myCompanySnapshot: firebase.firestore.DocumentSnapshot,
  myAccountType: tAccountType,
): tReduxAction => {
  return (dispatch) => {
    try {
      if (!myCompanySnapshot.exists) {
        throw new Error("Couldn't find the company!");
      }

      if (myAccountType === "brand") {
        const brandData = _.merge<tBrand, { type: "brand" }>(validate("tBrand")(myCompanySnapshot.data()), {
          type: "brand",
        });
        dispatch({
          type: "ADMIN_SET_COMPANY",
          company: brandData,
        });
      } else if (myAccountType === "retailer") {
        const retailerData = _.merge<tBoutique, { type: "retailer" }>(validate("tBoutique")(myCompanySnapshot.data()), {
          type: "retailer",
        });
        dispatch({
          type: "ADMIN_SET_COMPANY",
          company: retailerData,
        });
      } else if (myAccountType === "rep") {
        const repData = _.merge<tRep, { type: "rep" }>(validate("tRep")(myCompanySnapshot.data()), { type: "rep" });
        dispatch({
          type: "ADMIN_SET_COMPANY",
          company: repData,
        });
      }

      dispatch({
        type: "SET_LOADING_COMPANY",
        loading: false,
      });
    } catch (error) {
      if (error instanceof Error) {
        dispatch(handleErrorEvents(error.message, "error", error));
      }
    }
    return undefined;
  };
};

export const useFetchCompanySettings = (id?: string, type?: "brands" | "reps" | "boutiques"): void => {
  const dispatch = useAppDispatch();
  const aCompanyRefPath = useSelector(({ UserReducer }) => UserReducer.profile.companyRefPath);
  const aAdminCompanyPath = id && type ? `${type}/${id}` : undefined;
  const aAccountType = useSelector(({ UserReducer }) => UserReducer.profile.accountType);
  const isFocused = useIsFocused();

  useEffect(() => {
    if (!isFocused) {
      dispatch({ type: "ADMIN_RESET_SETTINGS" });
    }
  }, [dispatch, isFocused]);

  useEffect(() => {
    if (isFocused) {
      dispatch({
        type: "SET_LOADING_COMPANY",
        loading: true,
      });

      firebase
        .firestore()
        .doc(aAdminCompanyPath ? aAdminCompanyPath : aCompanyRefPath)
        .onSnapshot((myCompanySnapshot) => {
          if (aAccountType === "tmtAdmin") {
            if (type === "brands") {
              dispatch(fetchCompanyData(myCompanySnapshot, "brand"));
            }
            if (type === "boutiques") {
              dispatch(fetchCompanyData(myCompanySnapshot, "retailer"));
            }
            if (type === "reps") {
              dispatch(fetchCompanyData(myCompanySnapshot, "rep"));
            }
          } else {
            dispatch(fetchCompanyData(myCompanySnapshot, aAccountType));
          }
        });
    }
  }, [aCompanyRefPath, aAccountType, dispatch, aAdminCompanyPath, id, type, isFocused]);
};

export const setRetailInfo = (brandId: string): tReduxAction => {
  return (dispatch) => {
    api
      .get({ endpoint: `/admin/retailer/${brandId}` })
      .then((myData) => {
        const myBrand = validate("tBoutique")(myData.data);
        dispatch({ type: "ADMIN_SET_COMPANY", company: { ...myBrand, type: "retailer" } });
      })
      .catch((e) => {
        console.error(e);
        dispatch({ type: "RIBBON", message: new Error("Failed to retrieve Brand information") });
      });
  };
};

export const sendInfo = (adminSettingsParams: tAdminCompanyState, adminUserParams: tAdminUserState): tReduxAction => {
  return (dispatch) => {
    const data = _.cloneDeep(adminSettingsParams);
    const myUser = adminUserParams;

    if (data.type === "brand") {
      data.website = getSafeUrl(data.website);
      data.links = _.mapValues(data.links, getSafeUrl);

      dispatch({ type: "SET_UPDATING_PROFILE", loading: true });

      putBrand(data.id, { brand: data, user: myUser })
        .then(() => {
          dispatch({ type: "RIBBON", message: "Successfully updated Brand information" });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        })
        .catch((e) => {
          console.error(e);
          dispatch({ type: "RIBBON", message: new Error("Failed to update Brand information") });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        });
    } else if (data.type === "retailer") {
      data.website = getSafeUrl(data.website);
      data.links = _.mapValues(data.links, getSafeUrl);

      dispatch({ type: "SET_UPDATING_PROFILE", loading: true });
      putRetailer(data.id, { retailer: data, user: myUser })
        .then(() => {
          dispatch({ type: "RIBBON", message: "Successfully updated Retailer information" });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        })
        .catch((e) => {
          console.error(e);
          dispatch({ type: "RIBBON", message: new Error("Failed to update Retailer information") });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        });
    } else if (data.type === "rep") {
      data.socialMedia = _.mapValues(data.socialMedia, getSafeUrl);

      dispatch({ type: "SET_UPDATING_PROFILE", loading: true });
      putRep(data.id, { rep: data, user: myUser })
        .then(() => {
          dispatch({ type: "RIBBON", message: "Successfully updated Sales Rep information" });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        })
        .catch((e) => {
          console.error(e);
          dispatch({ type: "RIBBON", message: new Error("Failed to update Sales Rep information") });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        });
    }
  };
};

export const updateRetailer = (retailer: tBoutique, user: tUser): tReduxAction => {
  return async (dispatch) => {
    try {
      await putRetailer(retailer.id, { retailer, user });
      dispatch({ type: "RIBBON", message: "Successfully updated Retailer information" });
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
    }
  };
};

export const releaseRetailerCredit = (retailerId: string): tReduxAction => {
  return async (dispatch) => {
    try {
      await putRetailerReleaseCredit(retailerId);
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
    }
  };
};

export const uploadBannerImage = (adminSettingsParams: tAdminCompanyState): tReduxAction => {
  return (dispatch) => {
    const companyState = adminSettingsParams;
    if (companyState.type === "brand") {
      const { id } = companyState;
      const rBrandProfile = companyState;
      return DocumentPicker.getDocumentAsync({ type: "image/png, image/jpeg, image/webp" })
        .then((myUploadedImage) => {
          if (myUploadedImage.type === "success" && myUploadedImage.file) {
            const kFileExt = myUploadedImage.file.type.replace(/^.*\//gm, "");
            const aImageRef = firebase.storage().ref(`brands/${id}/uploads/banner.${kFileExt}`);
            return aImageRef.put(myUploadedImage.file);
          } else {
            throw new Error("brand id is undefined");
          }
        })
        .then((myUploadResults) => {
          return myUploadResults.ref.getDownloadURL();
        })
        .then((url) => {
          const tempImages = _.cloneDeep(rBrandProfile?.images);
          tempImages.bannerDownloadURL = url;
          dispatch({
            type: "RIBBON",
            message: "Successfully uploaded banner image. Be sure to Save before leaving page.",
          });
          dispatch({
            type: "ADMIN_UPDATE_BRAND_SETTINGS",
            settingsPart: { images: tempImages },
          });
        })
        .catch((e: Error) => {
          dispatch(handleErrorEvents(e.message, "error", e));
        });
    }
    return Promise.resolve();
  };
};

export const uploadProfileImage = (adminSettingsParams: tAdminCompanyState, file?: File): tReduxAction => {
  return (dispatch) => {
    const companyState = adminSettingsParams;
    const kUserType = companyState.type;
    let aId: string;
    if (kUserType === "none") {
      throw new Error("Invalid user type: none");
    } else if (kUserType === "tmtAdmin") {
      aId = companyState.uid;
    } else {
      aId = companyState.id;
    }
    Promise.resolve()
      .then(() => {
        if (file) {
          let aImageRef: firebase.storage.Reference;
          const kFileExt = file.type.replace(/^.*\//gm, "");
          if (kUserType === "brand") {
            aImageRef = firebase.storage().ref(`brands/${aId}/uploads/profile.${kFileExt}`);
          } else if (kUserType === "rep") {
            aImageRef = firebase.storage().ref(`rep/${aId}/uploads/profile.${kFileExt}`);
          } else if (kUserType === "tmtAdmin") {
            aImageRef = firebase.storage().ref(`tmtAdmin/uploads/profile.${kFileExt}`);
          } else {
            aImageRef = firebase.storage().ref(`retailer/${aId}/uploads/profile.${kFileExt}`);
          }
          return aImageRef.put(file);
        } else {
          throw new Error("brand id is undefined");
        }
      })
      .then((myUploadResults) => {
        dispatch({ type: "SET_UPLOADING_PROFILE_PIC", loading: true });
        return myUploadResults.ref.getDownloadURL();
      })
      .then((url) => {
        if (kUserType === "brand") {
          dispatch({
            type: "ADMIN_UPDATE_BRAND_SETTINGS",
            settingsPart: { images: { logoDownloadURL: url } },
          });
        } else if (kUserType === "rep") {
          dispatch({
            type: "ADMIN_UPDATE_REP_SETTINGS",
            settingsPart: { avatar: url },
          });
        } else if (kUserType === "retailer") {
          dispatch({
            type: "ADMIN_UPDATE_RETAIL_SETTINGS",
            settingsPart: { avatar: url },
          });
        }
      })
      .then(() => {
        dispatch({ type: "SET_UPLOADING_PROFILE_PIC", loading: false });
      })
      .catch((e: Error) => {
        console.error(e);
        dispatch(handleErrorEvents(e.message, "error", e));
      });
  };
};

export const uploadTermsAndConditions = (adminSettingsParams: tAdminCompanyState): tReduxAction => {
  return (dispatch) => {
    const companyState = adminSettingsParams;
    if (companyState.type === "brand") {
      const { id } = companyState;
      const aImageRef = firebase.storage().ref(`brands/${id}/uploads/terms_and_conditions.pdf`);
      const aProfileResult = (name: string, file: File) =>
        aImageRef
          .put(file)
          .then((myUploadResults) => {
            return myUploadResults.ref.getDownloadURL();
          })
          .then((url) => {
            return dispatch({
              type: "ADMIN_UPDATE_BRAND_SETTINGS",
              settingsPart: { termsAndConditions: { name, url } },
            });
          });
      DocumentPicker.getDocumentAsync({ type: "application/pdf" })
        .then((myUploadedPdf) => {
          if (myUploadedPdf.type === "success" && myUploadedPdf.file) {
            dispatch({
              type: "RIBBON",
              message: "File Uploaded! Be sure to Save before leaving page.",
            });
            aProfileResult(myUploadedPdf.name, myUploadedPdf.file);
          } else {
            dispatch(handleErrorEvents("Brand ID is undefined", "error", new Error("brand id is undefined")));
          }
        })
        .catch((e: Error) => {
          dispatch(handleErrorEvents(e.message, "error", e));
        });
    }
  };
};

export const uploadRetailTaxInfo = (myType: tAdminCompanyState["type"], myId: string | false): tReduxAction => {
  return (dispatch) => {
    // const companyState = adminSettingsParams;
    if (myType === "retailer") {
      const aImageRef = firebase.storage().ref(`retailer/${myId}/uploads/tax_info.pdf`);
      const aProfileResult = (name: string, file: File) =>
        aImageRef
          .put(file)
          .then((myUploads) => {
            return myUploads.ref.getDownloadURL();
          })
          .then((url) => {
            return dispatch({ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { taxInfo: { name, url } } });
          });
      DocumentPicker.getDocumentAsync({ type: "application/pdf" })
        .then((myUploadPdf) => {
          if (myUploadPdf.type === "success" && myUploadPdf.file) {
            dispatch({
              type: "RIBBON",
              message: "Successfully uploaded Tax Info. Be sure to Save before leaving page.",
            });
            aProfileResult(myUploadPdf.name, myUploadPdf.file);
          } else {
            dispatch(handleErrorEvents("Retailer ID is undefined", "error", new Error("Retailer id is undefined")));
          }
        })
        .catch((e: Error) => {
          dispatch(handleErrorEvents(e.message, "error", e));
        });
    }
  };
};

export const uploadRepTaxInfo = (adminSettingsParams: tAdminCompanyState): tReduxAction => {
  return (dispatch) => {
    const companyState = adminSettingsParams;
    if (companyState.type === "rep") {
      const { id } = companyState;
      const aImageRef = firebase.storage().ref(`rep/${id}/uploads/tax_info.pdf`);
      const aProfileResult = (name: string, file: File) =>
        aImageRef
          .put(file)
          .then((myUploads) => {
            return myUploads.ref.getDownloadURL();
          })
          .then((url) => {
            return dispatch({ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { taxInfo: { name, url } } });
          });
      DocumentPicker.getDocumentAsync({ type: "application/pdf" })
        .then((myUploadPdf) => {
          if (myUploadPdf.type === "success" && myUploadPdf.file) {
            dispatch({
              type: "RIBBON",
              message: "Successfully uploaded Tax Info. Be sure to Save before leaving page.",
            });
            aProfileResult(myUploadPdf.name, myUploadPdf.file);
          } else {
            dispatch(handleErrorEvents("Rep ID is undefined", "error", new Error("Rep id is undefined")));
          }
        })
        .catch((e: Error) => {
          dispatch(handleErrorEvents(e.message, "error", e));
        });
    }
  };
};
